import {DataFetcher, useRequest} from '@aboutyou/data-fetcher';
import {
  useIsFeatureActive,
  useIsSoftFeatureToggleActive,
} from '@aboutyou/feature-flags';
import {useRouter} from '@aboutyou/legacy-desktop/src/app/helpers/navigation';
import {PageTrackingContextProvider} from '@aboutyou/legacy-desktop/src/app/helpers/tracking/PageTrackingContext';
import {useTracking} from '@aboutyou/legacy-desktop/src/app/helpers/tracking/hooks/useTrackingContextAndClientV6';
import {useTrackingPageUrl} from '@aboutyou/legacy-desktop/src/app/helpers/tracking/hooks/useTrackingPageUrl';
import TrackContentPageView from '@aboutyou/legacy-desktop/src/app/helpers/tracking/trackerComponents/TrackContentPageView';
import useHelpCenterTranslations from '@aboutyou/legacy-desktop/src/app/pages/HelpCenter/useHelpCenterTranslations';
import {useService} from '@aboutyou/legacy-desktop/src/app/services/utils/context';
import {useUserStore} from '@aboutyou/legacy-desktop/src/app/store/context';
import {useLocale} from '@aboutyou/legacy-desktop/src/app/store/features/locale/useLocale';
import LegacyPage from '@aboutyou/legacy-shared/src/pages/HelpCenter/HelpCenterSectionPage';
import {HelpCenterEndpoint} from '@aboutyou/legacy-shared/src/pages/HelpCenter/helpCenterEndpoint';
import {loadHelpCenterSectionPageData} from '@aboutyou/legacy-shared/src/pages/HelpCenter/loadHelpCenterPageData';
import {
  HelpCenterPageProps,
  HelpCenterSectionPageData,
} from '@aboutyou/legacy-shared/src/pages/HelpCenter/types';
import {TrackingPageTypeWithId} from '@aboutyou/legacy-tracking-sdk';
import {Redirect} from '@aboutyou/router';
import React, {FC} from 'react';
import {withLegacyProviders} from '../../LegacyComponentWrapper';
import {useGetActiveSectionId} from './hooks/useGetActiveSectionId';
import {useGetCannonicalAndAlternateLink} from './hooks/useGetCannonicalAndAlternateLink';

const ConnectedHelpCenterSectionPage: React.FC<
  React.PropsWithChildren
> = () => {
  const {TrackingClient} = useTracking();
  const {navigateClient} = useRouter();

  const isVip = useUserStore(store => store.isVip);
  const sectionId = useGetActiveSectionId();

  const {path, search} = useTrackingPageUrl();

  const {helpCenter: endpoints} = useService();
  const {locale} = useLocale();
  const fetchDataRequest = useRequest(
    `helpCenterSectionsLegacy-${sectionId}`,
    () => {
      return loadHelpCenterSectionPageData(endpoints, locale, sectionId);
    },
  );

  const isShowOldHelpCenterEnabled =
    useIsSoftFeatureToggleActive('show_old_help_center') === true;

  const isCoinsActive = useIsFeatureActive('ff_coins_rollout').isActive;

  return (
    <PageTrackingContextProvider
      id={sectionId}
      path={path}
      search={search}
      type={TrackingPageTypeWithId.HelpCenterSection}
    >
      <DataFetcher
        request={fetchDataRequest}
        View={LegacyPageView}
        Error={() => <Redirect to="/h" />}
        viewProps={
          {
            locale,
            endpoints: endpoints as HelpCenterEndpoint,
            activeSectionId: sectionId,
            trackingClient: TrackingClient,
            navigateClient,
            isVip,
            isCoinsActive,
            isShowOldHelpCenterEnabled,
          } as HelpCenterPageProps<HelpCenterSectionPageData>
        }
      />
    </PageTrackingContextProvider>
  );
};

const LegacyPageView: FC<
  Omit<
    HelpCenterPageProps<HelpCenterSectionPageData>,
    'translations' | 'canonicalUrl' | 'alternateLink'
  >
> = props => {
  const translations = useHelpCenterTranslations({
    activeSectionName: props.activeSection?.name,
  });
  const {canonicalUrl, alternateLink} = useGetCannonicalAndAlternateLink(
    props.activeSection,
  );

  return (
    <>
      <TrackContentPageView
        pageTitle={translations.sectionPage.seo_page_title}
      />
      <LegacyPage
        {...props}
        translations={translations}
        canonicalUrl={canonicalUrl}
        alternateLink={alternateLink}
        isShowOldHelpCenterEnabled={props.isShowOldHelpCenterEnabled}
        isCoinsActive={props.isCoinsActive}
      />
    </>
  );
};

export const SectionPage = withLegacyProviders(ConnectedHelpCenterSectionPage);
